/**
 * KisiPanelBolumleri — geçici kişi panelinin bölümleri (backlog #646; backend #645): Eksikler listesi, Bilgilerim, Belgelerim, KVKK, Mesleklerim.
 * Her bölüm KENDİ verisini okur (tek bir kişi: oturumdan gelir, istekte kimlik yok) ve bir değişiklikten sonra `onDegisti()` ile üst sayfayı uyarır
 * (eksikler listesi yenilensin). Oturum biterse (`oturumBitti`) `onOturumBitti()`. Hiçbir bölüm personel API'sine gitmez.
 * Telefon öncelikli: tek sütun, büyük dokunma alanları.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Form, Input, Label, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import GuvenliHtml from '../../../Components/Common/GuvenliHtml';
import YuklemeDugmesi from './YuklemeDugmesi';
import {
  belgeleriOku, belgeYukle, bilgiGuncelle, kvkkKabul, kvkkOku, meslekEkle, meslekleriOku, sertifikaYukle,
} from '../../../services/kisiPanelService';
import { currentLocaleTag } from '../../../i18n';

const RENK = { MISSING: 'danger', PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger' };
const tarih = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(currentLocaleTag()) : '';
};

export function DurumRozeti({ durum }) {
  const { t } = useTranslation('kisiPanel');
  return <Badge color={RENK[durum] || 'secondary'} data-testid={`durum-${durum}`}>{t(`durum.${durum}`, { defaultValue: durum })}</Badge>;
}

/** Bir bölümün kendi verisini okuyan küçük kanca: { veri, yukleniyor, hata, yenile }. */
function useBolum(okuyucu, onOturumBitti) {
  const { t } = useTranslation('kisiPanel');
  const [durum, setDurum] = useState({ veri: null, yukleniyor: true, hata: null });
  const [say, setSay] = useState(0);
  useEffect(() => {
    let iptal = false;
    async function calistir() {
      try {
        const v = await okuyucu();
        if (!iptal) setDurum({ veri: v, yukleniyor: false, hata: null });
      } catch (e) {
        if (iptal) return;
        if (e.oturumBitti) { onOturumBitti(); return; }
        setDurum((d) => ({ ...d, yukleniyor: false, hata: e.network ? t('hata.baglanti') : t('hata.genel') }));
      }
    }
    calistir();
    return () => { iptal = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [say]);
  return { ...durum, yenile: () => setSay((s) => s + 1) };
}

// ── Eksikler listesi ──────────────────────────────────────────────────────
export function EksiklerListesi({ liste }) {
  const { t } = useTranslation('kisiPanel');
  const kalan = liste.filter((m) => m.status !== 'APPROVED').length;
  return (
    <Card data-testid="bolum-liste">
      <CardHeader><h5 className="card-title mb-0">{t('liste.baslik')}</h5></CardHeader>
      <CardBody>
        {liste.length === 0 && <div className="text-muted">{t('liste.bos')}</div>}
        {liste.length > 0 && kalan === 0 && <Alert color="success" fade={false} className="mb-3" data-testid="liste-tamam">{t('liste.hepsiTamam')}</Alert>}
        {liste.length > 0 && kalan > 0 && <p className="text-muted" data-testid="liste-ilerleme">{t('liste.kalan', { kalan, toplam: liste.length })}</p>}
        <ul className="list-unstyled mb-0">
          {liste.map((m) => (
            <li key={m.key} className="d-flex justify-content-between align-items-start gap-2 py-2 border-bottom" data-testid={`liste-${m.key}`}>
              <div>
                <div className="fw-semibold">{m.title}</div>
                {m.status === 'REJECTED' && m.reason && <div className="small text-danger">{t('belge.redNedeni', { neden: m.reason })}</div>}
              </div>
              <DurumRozeti durum={m.status} />
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

// ── Bilgilerim ────────────────────────────────────────────────────────────
export function Bilgilerim({ kisi, onDegisti, onOturumBitti }) {
  const { t } = useTranslation('kisiPanel');
  const [telefon, setTelefon] = useState(kisi.phone || '');
  const [eposta, setEposta] = useState(kisi.email || '');
  const [kaydediyor, setKaydediyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [tamam, setTamam] = useState(false);
  const degisti = telefon !== (kisi.phone || '') || eposta !== (kisi.email || '');

  async function kaydet(e) {
    e.preventDefault();
    if (!degisti) return;
    setKaydediyor(true);
    setHata(null);
    setTamam(false);
    try {
      await bilgiGuncelle({ phone: telefon.trim(), email: eposta.trim() });
      setTamam(true);
      await onDegisti();
    } catch (err) {
      if (err.oturumBitti) { onOturumBitti(); return; }
      setHata(err.network ? t('hata.baglanti') : err.message || t('hata.genel'));
    } finally {
      setKaydediyor(false);
    }
  }

  return (
    <Card data-testid="bolum-bilgi">
      <CardHeader><h5 className="card-title mb-0">{t('bilgi.baslik')}</h5></CardHeader>
      <CardBody>
        <div className="mb-3">
          <div className="text-muted small">{t('bilgi.adSoyad')}</div>
          <div className="fw-semibold" data-testid="bilgi-ad">{kisi.ad} {kisi.soyad}</div>
          <div className="form-text">{t('bilgi.kimlikNotu')}</div>
        </div>
        <Form onSubmit={kaydet}>
          <div className="mb-3">
            <Label htmlFor="kp-telefon">{t('bilgi.telefon')}</Label>
            <Input id="kp-telefon" type="tel" inputMode="tel" autoComplete="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} maxLength={30} />
          </div>
          <div className="mb-3">
            <Label htmlFor="kp-eposta">{t('bilgi.eposta')}</Label>
            <Input id="kp-eposta" type="email" inputMode="email" autoComplete="email" value={eposta} onChange={(e) => setEposta(e.target.value)} maxLength={100} />
          </div>
          {hata && <Alert color="danger" fade={false} className="py-2" data-testid="bilgi-hata">{hata}</Alert>}
          {tamam && !hata && <Alert color="success" fade={false} className="py-2" data-testid="bilgi-tamam">{t('bilgi.kaydedildi')}</Alert>}
          <Button color="primary" type="submit" className="w-100" id="kp-bilgi-kaydet" disabled={kaydediyor || !degisti}>{kaydediyor ? <Spinner size="sm" /> : t('bilgi.kaydet')}</Button>
        </Form>
      </CardBody>
    </Card>
  );
}

// ── Belgelerim ────────────────────────────────────────────────────────────
export function Belgelerim({ onDegisti, onOturumBitti }) {
  const { t } = useTranslation('kisiPanel');
  const { veri, yukleniyor, hata, yenile } = useBolum(belgeleriOku, onOturumBitti);
  const dosyalar = (veri && veri.files) || [];
  const turler = (veri && veri.types) || [];
  const sonDosya = (typeId) => dosyalar.filter((d) => d.type_id === typeId).sort((a, b) => String(b.uploaded_at).localeCompare(String(a.uploaded_at)))[0] || null;

  return (
    <Card data-testid="bolum-belgeler">
      <CardHeader><h5 className="card-title mb-0">{t('belge.baslik')}</h5></CardHeader>
      <CardBody>
        {yukleniyor && <div className="text-center py-3"><Spinner /></div>}
        {hata && <Alert color="danger" fade={false} data-testid="belge-hata">{hata}</Alert>}
        {!yukleniyor && !hata && turler.length === 0 && <div className="text-muted">{t('belge.yok')}</div>}
        {turler.map((tur) => {
          const son = sonDosya(tur.type_id);
          const kilitli = tur.locked === true || (son && son.status === 'APPROVED');
          return (
            <div key={tur.type_id} className="border rounded p-3 mb-3" data-testid={`belge-${tur.type_id}`}>
              <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                <div>
                  <div className="fw-semibold">{tur.name}</div>
                  {tur.required && <Badge color="light" className="text-body">{t('belge.zorunlu')}</Badge>}
                </div>
                {son ? <DurumRozeti durum={son.status} /> : <DurumRozeti durum="MISSING" />}
              </div>
              {son && <div className="small text-muted mb-2">{son.file_name} · {tarih(son.uploaded_at)}</div>}
              {son && son.status === 'REJECTED' && son.reject_reason && <Alert color="danger" fade={false} className="py-2 small" data-testid={`belge-red-${tur.type_id}`}>{t('belge.redNedeni', { neden: son.reject_reason })}</Alert>}
              {kilitli
                ? <div className="small text-success" data-testid={`belge-kilitli-${tur.type_id}`}>{t('belge.kilitli')}</div>
                : (
                  <YuklemeDugmesi
                    kimlik={String(tur.type_id)} maxMb={tur.max_mb || 10}
                    onYukle={(dosya, ilerleme) => belgeYukle(tur.type_id, dosya, ilerleme)}
                    onBitti={async () => { yenile(); await onDegisti(); }}
                    onOturumBitti={onOturumBitti}
                  />
                )}
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}

// ── KVKK ──────────────────────────────────────────────────────────────────
export function KvkkMetinleri({ onDegisti, onOturumBitti }) {
  const { t } = useTranslation('kisiPanel');
  const { veri, yukleniyor, hata, yenile } = useBolum(kvkkOku, onOturumBitti);
  const [calisan, setCalisan] = useState(null);
  const [kabulHatasi, setKabulHatasi] = useState(null);
  const metinler = Array.isArray(veri) ? veri : [];

  async function kabul(key) {
    setCalisan(key);
    setKabulHatasi(null);
    try {
      await kvkkKabul(key);
      yenile();
      await onDegisti();
    } catch (e) {
      if (e.oturumBitti) { onOturumBitti(); return; }
      setKabulHatasi(e.network ? t('hata.baglanti') : e.message || t('hata.genel'));
    } finally {
      setCalisan(null);
    }
  }

  return (
    <Card data-testid="bolum-kvkk">
      <CardHeader><h5 className="card-title mb-0">{t('kvkk.baslik')}</h5></CardHeader>
      <CardBody>
        {yukleniyor && <div className="text-center py-3"><Spinner /></div>}
        {hata && <Alert color="danger" fade={false}>{hata}</Alert>}
        {kabulHatasi && <Alert color="danger" fade={false} data-testid="kvkk-hata">{kabulHatasi}</Alert>}
        {!yukleniyor && !hata && metinler.length === 0 && <div className="text-muted">{t('kvkk.yok')}</div>}
        {metinler.map((m) => (
          <div key={m.key} className="border rounded p-3 mb-3" data-testid={`kvkk-${m.key}`}>
            <div className="fw-semibold mb-2">{m.title}</div>
            <div className="border rounded p-2 mb-2 bg-light" style={{ maxHeight: 220, overflowY: 'auto' }}><GuvenliHtml html={m.body} /></div>
            {m.accepted
              ? <div className="small text-success" data-testid={`kvkk-kabul-${m.key}`}>{t('kvkk.kabulEdildi', { tarih: tarih(m.accepted_at) })}</div>
              : (
                <Button color="primary" className="w-100" id={`kvkk-kabul-dugme-${m.key}`} disabled={calisan === m.key} onClick={() => kabul(m.key)}>
                  {calisan === m.key ? <Spinner size="sm" /> : t('kvkk.kabulEt')}
                </Button>
              )}
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

// ── Mesleklerim ───────────────────────────────────────────────────────────
export function Mesleklerim({ onDegisti, onOturumBitti }) {
  const { t } = useTranslation('kisiPanel');
  const { veri, yukleniyor, hata, yenile } = useBolum(meslekleriOku, onOturumBitti);
  const [secili, setSecili] = useState('');
  const [calisan, setCalisan] = useState(false);
  const [eklemeHatasi, setEklemeHatasi] = useState(null);
  const benim = (veri && veri.mine) || [];
  const secilebilir = ((veri && veri.available) || []).filter((m) => !benim.some((b) => b.meslek_id === m.meslek_id));

  async function ekle() {
    if (!secili) return;
    setCalisan(true);
    setEklemeHatasi(null);
    try {
      await meslekEkle(Number(secili));
      setSecili('');
      yenile();
      await onDegisti();
    } catch (e) {
      if (e.oturumBitti) { onOturumBitti(); return; }
      setEklemeHatasi(e.network ? t('hata.baglanti') : e.message || t('hata.genel'));
    } finally {
      setCalisan(false);
    }
  }

  return (
    <Card data-testid="bolum-meslekler">
      <CardHeader><h5 className="card-title mb-0">{t('meslek.baslik')}</h5></CardHeader>
      <CardBody>
        {yukleniyor && <div className="text-center py-3"><Spinner /></div>}
        {hata && <Alert color="danger" fade={false}>{hata}</Alert>}
        {!yukleniyor && !hata && benim.length === 0 && <div className="text-muted mb-3">{t('meslek.yok')}</div>}
        {benim.map((m) => (
          <div key={m.meslek_id} className="border rounded p-3 mb-3" data-testid={`meslek-${m.meslek_id}`}>
            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
              <div className="fw-semibold">{m.name}</div>
              <DurumRozeti durum={m.status} />
            </div>
            <div className="small text-muted mb-1">{t('meslek.sertifika')}</div>
            {m.certificate && <div className="mb-2"><DurumRozeti durum={m.certificate.status} />{m.certificate.expires_at && <span className="small text-muted ms-2">{t('meslek.bitis', { tarih: tarih(m.certificate.expires_at) })}</span>}</div>}
            {m.certificate && m.certificate.status === 'REJECTED' && m.certificate.reject_reason && (
              <Alert color="danger" fade={false} className="py-2 small">{t('belge.redNedeni', { neden: m.certificate.reject_reason })}</Alert>
            )}
            {m.certificate && m.certificate.status === 'APPROVED'
              ? <div className="small text-success">{t('belge.kilitli')}</div>
              : (
                <YuklemeDugmesi
                  kimlik={`meslek-${m.meslek_id}`}
                  onYukle={(dosya, ilerleme) => sertifikaYukle(m.meslek_id, dosya, ilerleme)}
                  onBitti={async () => { yenile(); await onDegisti(); }}
                  onOturumBitti={onOturumBitti}
                />
              )}
          </div>
        ))}
        {secilebilir.length > 0 && (
          <div className="mt-2">
            <Label htmlFor="kp-meslek-sec">{t('meslek.ekle')}</Label>
            <div className="d-flex gap-2">
              <Input id="kp-meslek-sec" type="select" value={secili} onChange={(e) => setSecili(e.target.value)}>
                <option value="">{t('meslek.sec')}</option>
                {secilebilir.map((m) => <option key={m.meslek_id} value={m.meslek_id}>{m.name}</option>)}
              </Input>
              <Button color="primary" id="kp-meslek-ekle" disabled={!secili || calisan} onClick={ekle}>{calisan ? <Spinner size="sm" /> : t('meslek.ekleDugme')}</Button>
            </div>
            <div className="form-text">{t('meslek.onayNotu')}</div>
          </div>
        )}
        {eklemeHatasi && <Alert color="danger" fade={false} className="mt-2 mb-0 py-2" data-testid="meslek-hata">{eklemeHatasi}</Alert>}
      </CardBody>
    </Card>
  );
}
